import * as React from 'react';
import { defaultSyncConfig } from './plannerDefaults';

export const monthOptions = [
  { value: 1, label: 'Январь' },
  { value: 2, label: 'Февраль' },
  { value: 3, label: 'Март' },
  { value: 4, label: 'Апрель' },
  { value: 5, label: 'Май' },
  { value: 6, label: 'Июнь' },
  { value: 7, label: 'Июль' },
  { value: 8, label: 'Август' },
  { value: 9, label: 'Сентябрь' },
  { value: 10, label: 'Октябрь' },
  { value: 11, label: 'Ноябрь' },
  { value: 12, label: 'Декабрь' },
];

export function formatMoney(value, currency = 'RUB') {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatTimestamp(value) {
  if (!value) {
    return '';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function barHeight(value, maxValue) {
  return Math.max(4, (Number(value || 0) / maxValue) * 100);
}

export function normalizeDetail(detail) {
  return {
    ...detail,
    budget: {
      ...detail.budget,
      currency_code: detail.budget.currency_code || 'RUB',
      notes: detail.budget.notes || '',
    },
    items: (detail.items || []).map((item, index) => ({
      ...item,
      amount: item.amount ?? '0',
      notes: item.notes || '',
      month: item.month ?? null,
      sort_order: item.sort_order || index + 1,
      firefly_category_id: item.firefly_category_id ?? null,
      firefly_category_ids: item.firefly_category_ids || (item.firefly_category_id ? [item.firefly_category_id] : []),
      firefly_budget_id: item.firefly_budget_id ?? null,
      firefly_account_id: item.firefly_account_id ?? null,
      income_type: item.kind === 'income' ? (item.income_type || 'salary') : '',
      income_owner: item.income_owner || '',
      firefly_bill_id: item.firefly_bill_id ?? null,
      firefly_rule_ids: item.firefly_rule_ids || [],
      create_firefly_bill: Boolean(item.create_firefly_bill),
      sync_to_firefly: Boolean(item.sync_to_firefly),
      last_synced_at: item.last_synced_at || null,
      last_sync_status: item.last_sync_status || '',
      last_sync_summary: item.last_sync_summary || '',
    })),
    monthly_plan: detail.monthly_plan || [],
    item_summaries: detail.item_summaries || [],
    firefly_syncable: detail.firefly_syncable || [],
    sync_config: {
      ...defaultSyncConfig,
      ...(detail.sync_config || {}),
    },
    sync_runs: detail.sync_runs || [],
  };
}

export function MonthlyAnalyticsCharts({ rows }) {
  if (!rows.length) {
    return null;
  }
  return (
    <div className="analytics-grid">
      <article className="mini-chart-card">
        <h3>Отклонение расходов</h3>
        <VarianceStrip rows={rows} valueKey="expense_variance" reverse />
      </article>
      <article className="mini-chart-card">
        <h3>Остаток по месяцам</h3>
        <VarianceStrip rows={rows} valueKey="balance_variance" />
      </article>
    </div>
  );
}

export function VarianceStrip({ rows, valueKey, reverse = false }) {
  const maxValue = Math.max(1, ...rows.map((row) => Math.abs(Number(row[valueKey] || 0))));
  return (
    <div className="variance-strip">
      {rows.map((row) => {
        const value = Number(row[valueKey] || 0);
        const isRisk = reverse ? value > 0 : value < 0;
        const label = monthOptions.find((month) => month.value === row.month)?.label?.slice(0, 3) || row.label;
        return (
          <div key={`${valueKey}-${row.month}`} className="variance-column">
            <span
              className={`variance-bar chart-tooltip ${isRisk ? 'risk' : 'ok'}`}
              style={{ height: `${barHeight(Math.abs(value), maxValue)}%` }}
              data-tooltip={`${label}: ${formatMoney(value)}`}
            />
            <small>{label}</small>
          </div>
        );
      })}
    </div>
  );
}

export function computeExpenseAnalytics(summaries, selectedMonth = null) {
  const expenseSummaries = summaries.filter((s) => s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit');
  const typeLabels = { recurring: 'Периодические', one_time: 'Единоразовые', limit: 'Лимиты' };
  const typeTotals = { recurring: { planned: 0, actual: 0, variance: 0 }, one_time: { planned: 0, actual: 0, variance: 0 }, limit: { planned: 0, actual: 0, variance: 0 } };
  const monthlyByType = {};
  for (let m = 1; m <= 12; m++) {
    monthlyByType[m] = { recurring: { planned: 0, actual: 0 }, one_time: { planned: 0, actual: 0 }, limit: { planned: 0, actual: 0 } };
  }
  for (const item of expenseSummaries) {
    const breakout = item.monthly_breakout || [];
    if (selectedMonth != null) {
      for (const mb of breakout) {
        if (mb.month === selectedMonth) {
          typeTotals[item.kind].planned += Number(mb.planned || 0);
          typeTotals[item.kind].actual += Number(mb.actual || 0);
          monthlyByType[mb.month][item.kind].planned += Number(mb.planned || 0);
          monthlyByType[mb.month][item.kind].actual += Number(mb.actual || 0);
        }
      }
    } else {
      typeTotals[item.kind].planned += Number(item.planned_total || 0);
      typeTotals[item.kind].actual += Number(item.actual_total || 0);
      typeTotals[item.kind].variance += Number(item.variance || 0);
      for (const mb of breakout) {
        if (mb.month >= 1 && mb.month <= 12) {
          monthlyByType[mb.month][item.kind].planned += Number(mb.planned || 0);
          monthlyByType[mb.month][item.kind].actual += Number(mb.actual || 0);
        }
      }
    }
  }
  if (selectedMonth != null) {
    for (const kind of ['recurring', 'one_time', 'limit']) {
      typeTotals[kind].variance = typeTotals[kind].actual - typeTotals[kind].planned;
    }
  }
  const monthlyRows = [];
  for (let m = 1; m <= 12; m++) {
    const data = monthlyByType[m];
    monthlyRows.push({ month: m, ...data });
  }
  return { typeTotals, monthlyRows, typeLabels };
}

export function ExpenseTypeSummaryChart({ typeTotals, typeLabels, currencyCode = 'RUB' }) {
  const types = ['recurring', 'one_time', 'limit'];
  const maxValue = Math.max(1, ...types.flatMap((t) => [typeTotals[t].planned, typeTotals[t].actual]));
  return (
    <div className="expense-type-summary">
      {types.map((kind) => {
        const t = typeTotals[kind];
        const overrun = t.actual > t.planned;
        return (
          <div key={kind} className="expense-type-card">
            <div className="expense-type-card-head">
              <h4>{typeLabels[kind]}</h4>
              <div className="expense-type-amounts">
                <span className="expense-type-plan">План: {formatMoney(t.planned, currencyCode)}</span>
                <span className={overrun ? 'risk-value' : 'good-value'}>Факт: {formatMoney(t.actual, currencyCode)}</span>
              </div>
            </div>
            <div className="expense-type-bars">
              <div className="expense-bar-track">
                <span
                  className="expense-bar expense-bar-plan chart-tooltip"
                  style={{ width: `${Math.min(100, (t.planned / maxValue) * 100)}%` }}
                  data-tooltip={`План: ${formatMoney(t.planned, currencyCode)}`}
                />
              </div>
              <div className="expense-bar-track">
                <span
                  className={`expense-bar expense-bar-actual chart-tooltip ${overrun ? 'expense-bar-risk' : ''}`}
                  style={{ width: `${Math.min(100, (t.actual / maxValue) * 100)}%` }}
                  data-tooltip={`Факт: ${formatMoney(t.actual, currencyCode)}`}
                />
              </div>
            </div>
            <div className="expense-type-variance">
              Отклонение:{' '}
              <span className={t.variance > 0 ? 'risk-value' : 'good-value'}>
                {formatMoney(t.variance, currencyCode)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function computeYearlyTable(summaries, kind) {
  const items = summaries.filter((s) => s.kind === kind);
  const rows = items.map((item) => {
    const planned = Number(item.planned_total || 0);
    const actual = Number(item.actual_total || 0);
    const breakout = item.monthly_breakout || [];
    const monthsWithData = breakout.filter((mb) => Number(mb.actual || 0) > 0).length;
    return {
      name: item.name,
      planned,
      actual,
      monthsWithData,
      avgPayment: monthsWithData > 0 ? actual / monthsWithData : 0,
      avgPayment12: actual / 12,
    };
  });
  rows.sort((a, b) => a.name.localeCompare(b.name));
  const totalPlanned = rows.reduce((sum, r) => sum + r.planned, 0);
  const totalActual = rows.reduce((sum, r) => sum + r.actual, 0);
  return { rows, totalPlanned, totalActual };
}

const PIE_COLORS = [
  '#b45309', '#d97706', '#e07b38', '#f59e0b',
  '#0891b2', '#0d9488', '#06b6d4', '#089b7a',
  '#7c3aed', '#8b5cf6', '#a855f7', '#6d28d9',
  '#d946ef', '#ec4899', '#f43f5e', '#e11d48',
];

export function computePieData(summaries) {
  const expenseItems = summaries.filter(
    (s) => (s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit') &&
      Number(s.actual_total || 0) > 0
  );
  const totalActual = expenseItems.reduce((sum, item) => sum + Number(item.actual_total || 0), 0);
  const sorted = [...expenseItems].sort((a, b) => Number(b.actual_total || 0) - Number(a.actual_total || 0));
  const pieData = sorted.map((item, idx) => {
    const actual = Number(item.actual_total || 0);
    return {
      item_id: item.item_id,
      name: item.name,
      kind: item.kind,
      actual,
      percentage: totalActual > 0 ? (actual / totalActual) * 100 : 0,
      color: PIE_COLORS[idx % PIE_COLORS.length],
    };
  });
  return { pieData, totalActual };
}

export function computeOverspendBars(summaries) {
  const items = summaries.filter(
    (s) => (s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit') &&
      (Number(s.actual_total || 0) > 0 || Number(s.planned_total || 0) > 0)
  );
  const rows = items.map((item) => ({
    name: item.name,
    kind: item.kind,
    planned: Number(item.planned_total || 0),
    actual: Number(item.actual_total || 0),
    variance: Number(item.variance || 0),
  }));
  rows.sort((a, b) => b.variance - a.variance);
  const maxValue = Math.max(1, ...rows.flatMap((r) => [r.planned, r.actual]));
  return { rows, maxValue };
}

export function computeHeatmapData(summaries) {
  const items = summaries.filter(
    (s) => s.kind === 'recurring' || s.kind === 'one_time' || s.kind === 'limit'
  );
  const result = items.map((item) => {
    const breakout = item.monthly_breakout || [];
    const months = [];
    for (let m = 1; m <= 12; m++) {
      const mb = breakout.find((b) => b.month === m);
      const planned = Number(mb?.planned || 0);
      const actual = Number(mb?.actual || 0);
      const overspend = Math.max(0, actual - planned);
      months.push({ month: m, overspend });
    }
    const totalOverspend = months.reduce((sum, m) => sum + m.overspend, 0);
    return { name: item.name, kind: item.kind, months, totalOverspend };
  });
  result.sort((a, b) => b.totalOverspend - a.totalOverspend);
  const maxOverspend = Math.max(1, ...result.flatMap((item) => item.months.map((m) => m.overspend)));
  return { items: result, maxOverspend };
}

export function ExpenseTypeMonthlyChart({ monthlyRows, typeLabels, currencyCode = 'RUB' }) {
  const types = ['recurring', 'one_time', 'limit'];
  const maxValue = Math.max(
    1,
    ...monthlyRows.flatMap((r) => types.flatMap((t) => [r[t].planned, r[t].actual])),
  );
  return (
    <div className="expense-monthly-chart">
      {monthlyRows.map((row) => {
        const label = monthOptions.find((m) => m.value === row.month)?.label?.slice(0, 3) || row.month;
        return (
          <div key={row.month} className="expense-monthly-column">
            <div className="expense-monthly-bars">
              {types.map((kind) => (
                <div key={kind} className="expense-monthly-group">
                  <span
                    className="expense-bar expense-monthly-bar expense-bar-plan chart-tooltip"
                    style={{ height: `${Math.max(3, (row[kind].planned / maxValue) * 100)}%` }}
                    data-tooltip={`${typeLabels[kind]} план ${label}: ${formatMoney(row[kind].planned, currencyCode)}`}
                  />
                  <span
                    className={`expense-bar expense-monthly-bar expense-bar-actual chart-tooltip ${row[kind].actual > row[kind].planned ? 'expense-bar-risk' : ''}`}
                    style={{ height: `${Math.max(3, (row[kind].actual / maxValue) * 100)}%` }}
                    data-tooltip={`${typeLabels[kind]} факт ${label}: ${formatMoney(row[kind].actual, currencyCode)}`}
                  />
                </div>
              ))}
            </div>
            <small className="chart-label">{label}</small>
          </div>
        );
      })}
    </div>
  );
}
